import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ImageUp, Loader2 } from 'lucide-react';
import { mettreAJourLogosTelegram } from '../services/api';

type Resultat = { cible: string; ok: boolean; message: string };

/**
 * Repose le logo de l'association sur le bot et sur chaque canal Telegram.
 *
 * L'application pose le logo d'elle-même quand un réglage Telegram change et
 * le jour où le logo est fourni. Mais une photo de canal se change aussi
 * depuis un téléphone, un canal peut avoir été recréé, et une pose peut avoir
 * échoué un jour où Telegram était injoignable : rien ne le signalerait. Ce
 * bouton vérifie et remet en état, sans qu'il faille se souvenir de ce que
 * l'application a déjà fait.
 *
 * Le compte rendu est donné destination par destination : « le bot est à jour
 * mais le canal association refuse » se corrige, « ça n'a pas marché » non.
 */
export default function LogosTelegram() {
  const [resultats, setResultats] = useState<Resultat[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const mettreAJour = useMutation({
    mutationFn: mettreAJourLogosTelegram,
    onSuccess: (d: any) => setResultats(d.resultats ?? []),
    onError: (e: Error) => setErreur(e.message),
  });

  return (
    <div className="flex flex-col items-end gap-1.5">
      <button
        type="button"
        onClick={() => {
          setResultats(null);
          setErreur(null);
          mettreAJour.mutate();
        }}
        disabled={mettreAJour.isPending}
        title="Vérifier que le bot et les canaux portent bien le logo de l'association"
        className="btn-outline inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 disabled:opacity-50"
      >
        {mettreAJour.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <ImageUp className="w-3.5 h-3.5" />
        )}
        Mettre à jour les logos
      </button>

      {erreur && (
        <p className="text-xs text-red-700 text-right max-w-[16rem]">{erreur}</p>
      )}

      {resultats?.map((r) => (
        <p
          key={r.cible}
          className={`text-xs flex items-start gap-1 text-right max-w-[16rem] ${
            r.ok ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
          }`}
        >
          {r.ok ? (
            <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
          )}
          <span>
            <span className="font-medium">{r.cible}</span> — {r.message}
          </span>
        </p>
      ))}
    </div>
  );
}
